import * as React from 'react';
import styled from 'styled-components';
import { InvoiceDescriptionStatus, PaymentMethod } from 'types/plansales/billing';
import { NULL_TEXT } from 'constants/common';
import {
  groupPlanMap,
  paymentInvoiceDetailStatusLabel,
  paymentMethodLabel,
} from 'constants/plansales/billing';
import { getBlankUtcAndKstTime, getUtcAndKstTime } from 'modules/billing';
import InformationTable from 'components/common/table/InformationTable';
import { InformationObject } from 'types/common/informationTable';
import { getUserNameFromEmail } from 'modules/utils/user';
import { GroupPlanType } from 'types/plansales/plan';
import { InvoiceDetailStatus } from 'types/plansales/billing/invoice';
import InvoiceDetailPaymentRetryModal from 'components/plansales/invoice/invoiceDetail/InvoiceDetailPaymentRetryModal';

interface Props {
  groupId?: string;
  groupName?: string;
  invoiceNo: string;
  paymentStatus: InvoiceDetailStatus;
  registeredDate: string;
  paymentSucceedDate: string | null;
  paymentRefundDate: string | null;
  adminEmail: string;
  refundRequestedBy?: string;
  last4CardNumber: string;
  paymentMethod: PaymentMethod;
  paymentId: string;
  avalaraDocCode: string;
  isPending: boolean;
  isRefunded: boolean;
  isVoid: boolean;
  planType: GroupPlanType;
  invoiceStatus: InvoiceDescriptionStatus;
  paymentInvoiceId: string | null;
  taxAmount: number;
  refetchInvoice: () => void;
}

const InvoiceInformation = ({
  groupId,
  groupName,
  invoiceNo,
  paymentStatus,
  registeredDate,
  paymentSucceedDate,
  paymentRefundDate,
  adminEmail,
  refundRequestedBy,
  paymentId,
  paymentMethod,
  avalaraDocCode,
  last4CardNumber,
  isPending,
  isRefunded,
  isVoid,
  planType,
  paymentInvoiceId,
  refetchInvoice,
}: Props) => {
  const showRefundOperator = isRefunded || isVoid;

  const pendingCheck = (value: string | React.ReactNode) => {
    return isPending || isVoid ? NULL_TEXT : value;
  };

  const paymentMethodText =
    paymentMethod === PaymentMethod.CREDITS
      ? paymentMethodLabel[PaymentMethod.CREDITS]
      : `${paymentMethodLabel[paymentMethod]} : Card Ending in ${last4CardNumber}`;

  const invoiceInformationData: InformationObject[] = [
    { title: 'Plan Type', render: groupPlanMap[planType]?.replace('INV', ''), titleWidth: '30%' },
    {
      title: 'Payment Method',
      render: pendingCheck(paymentMethodText),
      titleWidth: '30%',
    },
    {
      title: 'Invoice No.',
      render: invoiceNo,
      titleWidth: '30%',
    },
    {
      title: 'Invoice Creation Date',
      render: getUtcAndKstTime(registeredDate),
      titleWidth: '30%',
    },
    { title: 'Group ID', render: groupId, titleWidth: '30%' },
    { title: 'Group Name', render: groupName, titleWidth: '30%' },
    { title: 'Admin Email', render: adminEmail, titleWidth: '30%' },
    {
      title: 'Payment Status',
      render: showRefundOperator ? (
        <>
          <PointText>{paymentInvoiceDetailStatusLabel[paymentStatus]}</PointText>
          <br />
          Confirmed by <Capitalize>{getUserNameFromEmail(refundRequestedBy)}</Capitalize>
          <br />
          {paymentRefundDate ? getUtcAndKstTime(paymentRefundDate) : getBlankUtcAndKstTime()}
        </>
      ) : (
        <>
          {paymentInvoiceDetailStatusLabel[paymentStatus]}
          {paymentStatus === InvoiceDetailStatus.Open && (
            <InvoiceDetailPaymentRetryModal invoiceId={invoiceNo} refetchInvoice={refetchInvoice} />
          )}
        </>
      ),
      titleWidth: '30%',
    },
    {
      title: 'Payment Invoice No.',
      render: pendingCheck(paymentInvoiceId),
      titleWidth: '30%',
    },
    {
      title: 'PaymentTxId',
      render: pendingCheck(paymentId),
      titleWidth: '30%',
    },
    {
      title: 'AvalaraDocCode',
      render: pendingCheck(avalaraDocCode),
      titleWidth: '30%',
    },
    {
      title: 'Payment Date',
      render: paymentSucceedDate ? getUtcAndKstTime(paymentSucceedDate) : getBlankUtcAndKstTime(),
      titleWidth: '30%',
    },
  ];
  return <InformationTable title={'Info'} data={invoiceInformationData} />;
};

export default InvoiceInformation;

const PointText = styled.span`
  font-weight: 600;
  color: red;
`;

export const Capitalize = styled.span`
  text-transform: capitalize;
`;
